import { apiClient } from '@/lib/api-client';
import { logger } from '@/lib/logger';

export type PermissionScope = 'all' | 'own' | 'none';

export interface PermissionActions {
  create?: PermissionScope;
  read?: PermissionScope;
  update?: PermissionScope;
  delete?: PermissionScope;
  list?: PermissionScope;
}

export interface PermissionsResponse {
  role: string;
  effective_role: string;
  features: Record<string, PermissionActions>;
}

const PERMISSIONS_ME_URL = 'permissions/me/';

const permissionFailureStatus = (error: unknown) => {
  if (typeof error !== 'object' || error === null || !('response' in error)) return undefined;
  const status = (error as { response?: { status?: number } }).response?.status;
  return typeof status === 'number' ? status : undefined;
};

const isAbortError = (error: unknown) =>
  (typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    (error as { code?: string }).code === 'ERR_CANCELED') ||
  (error instanceof DOMException && error.name === 'AbortError');

export const getMyPermissions = async (signal?: AbortSignal): Promise<PermissionsResponse> => {
  try {
    const response = signal
      ? await apiClient.get<PermissionsResponse>(PERMISSIONS_ME_URL, { signal })
      : await apiClient.get<PermissionsResponse>(PERMISSIONS_ME_URL);
    return response.data;
  } catch (error) {
    if (!isAbortError(error)) {
      logger.error('Failed to load permissions', { status: permissionFailureStatus(error) });
    }
    throw error;
  }
};
